import { useCallback, useEffect, useRef, useState } from "react";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { cn } from "@/lib/utils";
import { useVisualViewport } from "@/shared/hooks/use-visual-viewport";
import { canvasToPngBytes, renderTypedSignaturePng } from "../domain/pdf";
import type { SignatureMethod } from "../types";
import drawSignatureIcon from "../assets/icon-draw-signature.svg";
import typeSignatureIcon from "../assets/icon-type-signature.svg";
import sheetCloseIcon from "../assets/icon-sheet-close.svg";

interface SignatureSheetProps {
  open: boolean;
  onClose: () => void;
  consentText: string | null;
  defaultName: string;
  onSigned: (png: Uint8Array, method: SignatureMethod) => void;
}

export function SignatureSheet({
  open,
  onClose,
  defaultName,
  onSigned,
}: SignatureSheetProps) {
  const [mode, setMode] = useState<SignatureMethod>("draw");
  const [typed, setTyped] = useState(defaultName);
  const [hasInk, setHasInk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const sized = useRef(false);
  const { keyboardOpen } = useVisualViewport();

  const sizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.25;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1a1613";
    sized.current = true;
  }, []);

  useEffect(() => {
    if (!open || mode !== "draw") return;
    const id = requestAnimationFrame(sizeCanvas);
    return () => cancelAnimationFrame(id);
  }, [open, mode, sizeCanvas]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!sized.current) sizeCanvas();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    canvas?.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
    setError(null);
  };

  const onPointerUp = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    sized.current = false;
    sizeCanvas();
    setHasInk(false);
    setTyped("");
    setError(null);
  };

  const canDone = mode === "draw" ? hasInk : typed.trim().length > 0;

  const handleDone = async () => {
    if (!canDone) {
      setError("Type your signature before tapping Done");
      return;
    }
    setError(null);
    const png =
      mode === "draw"
        ? await canvasToPngBytes(canvasRef.current!)
        : await renderTypedSignaturePng(typed.trim());
    onSigned(png, mode);
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Sign document"
      className="bg-white"
      titleClassName="border-0 bg-transparent"
      closeClassName="top-[18px] size-5 text-mf-neutral-900 hover:text-mf-neutral-900"
      closeIcon={
        <span className="relative size-5 overflow-hidden">
          <img src={sheetCloseIcon} alt="" className="size-full" />
        </span>
      }
    >
      <div className="flex flex-col gap-4 px-4 pb-4">
        <p
          className={cn(
            "text-center text-base font-normal leading-5 text-mf-neutral-600",
            keyboardOpen && mode === "typed" && "hidden",
          )}
        >
          We&apos;ve pre-filled the form using your details. Review it below, or
          check the full claim notice for more information
        </p>

        <div className="flex w-full flex-col gap-2">
          <div className="flex flex-col gap-2 rounded-[32px] bg-mf-neutral-50 p-2">
            <div className="flex h-[52px] rounded-[38px] bg-mf-neutral-100 p-1">
              <button
                type="button"
                onClick={() => {
                  setMode("draw");
                  setError(null);
                }}
                className={cn(
                  "flex h-11 flex-1 items-center justify-center gap-2 rounded-[28px] text-[15px] font-medium leading-5 text-mf-neutral-900",
                  mode === "draw" &&
                    "bg-white shadow-[0_0_8px_rgba(26,22,19,0.03)]",
                )}
              >
                <span className="relative size-5 overflow-hidden">
                  <img src={drawSignatureIcon} alt="" className="size-full" />
                </span>
                Draw Signature
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("typed");
                  setError(null);
                }}
                className={cn(
                  "flex h-11 flex-1 items-center justify-center gap-2 rounded-[28px] text-[15px] font-medium leading-5 text-mf-neutral-900",
                  mode === "typed" &&
                    "bg-white shadow-[0_0_8px_rgba(26,22,19,0.03)]",
                )}
              >
                <span className="relative size-5 overflow-hidden">
                  <img
                    src={typeSignatureIcon}
                    alt=""
                    className="size-full object-contain"
                  />
                </span>
                Type Signature
              </button>
            </div>

            {mode === "draw" ? (
              <canvas
                ref={canvasRef}
                className="h-[296px] w-full touch-none rounded-[24px] border border-mf-neutral-100 bg-white"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
            ) : (
              <>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => {
                    setTyped(e.target.value);
                    setError(null);
                  }}
                  placeholder="Type your full name"
                  className="h-[52px] w-full rounded-xl bg-white px-4 text-base leading-5 text-mf-neutral-900 shadow-[0_0_16px_rgba(26,22,19,0.03)] outline-none placeholder:text-[#c0b7ae]"
                />
                <div
                  className={cn(
                    "flex items-center justify-center overflow-hidden rounded-3xl bg-white px-4",
                    keyboardOpen ? "min-h-[88px] py-4" : "min-h-[180px] py-10",
                  )}
                >
                  <p
                    className={cn(
                      "w-full text-center font-display font-bold",
                      keyboardOpen
                        ? "text-[28px] leading-9"
                        : "text-[34px] leading-[44px]",
                      typed.trim() ? "text-mf-neutral-900" : "text-[#c0b7ae]",
                    )}
                  >
                    {typed.trim() || "YOUR NAME"}
                  </p>
                </div>
              </>
            )}
          </div>
          {error ? (
            <p className="w-full text-center text-xs font-normal leading-4 text-[#e53e3e]">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={clear}
            className="flex h-12 flex-1 items-center justify-center rounded-full border-2 border-mf-neutral-200 text-[15px] font-bold text-mf-neutral-900"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => void handleDone()}
            className="flex h-12 flex-1 items-center justify-center rounded-full bg-mf-neutral-900 text-[15px] font-bold text-white"
          >
            Done
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
